import { createContext, useCallback, useContext, useMemo, useState } from 'react'
import { createPortal } from 'react-dom'
import type { ReactNode } from 'react'
import { cx } from '../cx'
import { Icon, type IconName } from '../Icon'
import { Button } from '../control/Button'
import { prefersReducedMotion } from '../motion'

/** A leaving toast's row takes --t-slow to fold shut; it unmounts after. */
const FOLD_MS = 300

export interface ToastSpec {
  id: number
  icon?: IconName
  message: string
  action?: { label: string; onClick: () => void }
  timeout?: number
}

interface ToastApi {
  push: (t: Omit<ToastSpec, 'id'>) => void
  dismiss: (id: number) => void
}

const ToastContext = createContext<ToastApi | null>(null)

export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<Array<ToastSpec & { leaving?: boolean }>>([])

  // A toast leaves in two steps: it fades and its row folds shut, so the ones
  // under it slide into its place, then it is gone.
  const dismiss = useCallback((id: number) => {
    const drop = () => setItems((prev) => prev.filter((t) => t.id !== id))
    if (prefersReducedMotion()) return drop()
    setItems((prev) => prev.map((t) => (t.id === id ? { ...t, leaving: true } : t)))
    window.setTimeout(drop, FOLD_MS)
  }, [])

  const push = useCallback(
    (t: Omit<ToastSpec, 'id'>) => {
      const id = Date.now() + Math.floor(Math.random() * 1000)
      setItems((prev) => [...prev, { ...t, id }])
      window.setTimeout(() => dismiss(id), t.timeout ?? 5000)
    },
    [dismiss],
  )

  const api = useMemo(() => ({ push, dismiss }), [push, dismiss])

  return (
    <ToastContext.Provider value={api}>
      {children}
      {createPortal(
        <div className="toast-root" role="status" aria-live="polite">
          {items.map((t) => (
            <div key={t.id} className={cx('toast-slot', t.leaving && 'is-leaving')}>
              <div className="toast-fold">
                <div className="toast">
                  {t.icon && <Icon name={t.icon} />}
                  <span className="grow">{t.message}</span>
                  {t.action && (
                    <Button variant="ghost" size="sm" onClick={t.action.onClick}>
                      {t.action.label}
                    </Button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>,
        document.body,
      )}
    </ToastContext.Provider>
  )
}

export function useToast(): ToastApi {
  const ctx = useContext(ToastContext)
  if (!ctx) throw new Error('useToast must be used inside ToastProvider')
  return ctx
}
